import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Wordmark } from "@/components/site/Chrome";

export function examTitle(cert, session) {
  if (!cert) return "";
  const code = cert.exam_code || cert.version_tag || "";
  const label = session.mode === "free" ? "Free Sample Exam" : `Practice Exam ${session.exam_number}`;
  return `${cert.name}${code ? ` ${code}` : ""} — ${label}`;
}

function Navigator({ session, pos, onJump }) {
  const answered = Object.keys(session.answers).filter((k) => session.answers[k] !== undefined && session.answers[k] !== null).length;
  return (
    <div className="er-navigator" data-testid="exam-question-grid">
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <p className="er-label" data-testid="exam-navigator-summary">QUESTION NAVIGATOR · {answered} ANSWERED · {session.marked.length} FLAGGED</p>
          <div className="er-legend">
            <span><i style={{ background: "var(--ac)" }} />CURRENT</span>
            <span><i style={{ background: "var(--ac-tint)", border: "1px solid var(--ac-soft)" }} />ANSWERED</span>
            <span><i style={{ background: "#FFFFFF", border: "1px solid var(--field)" }} />UNSEEN</span>
            <span><i style={{ background: "#FFFFFF", border: "1px solid var(--ink)" }} />FLAGGED</span>
          </div>
        </div>
        <div className="er-nav-grid">
          {Array.from({ length: session.total_questions }, (_, i) => i + 1).map((n) => {
            const ans = session.answers[String(n)] !== undefined && session.answers[String(n)] !== null;
            const cls = n === pos ? "current" : session.marked.includes(n) ? "flagged" : ans ? "answered" : "";
            return <button key={n} data-testid={`exam-grid-q-${n}`} onClick={() => onJump(n)} className={`er-nav-cell ${cls}`}>{n}</button>;
          })}
        </div>
      </div>
    </div>
  );
}

function SubmitDialog({ answered, total, onCancel, onConfirm }) {
  return (
    <div className="er-modal">
      <div className="er-modal-box er-fade" data-testid="exam-submit-dialog">
        <div className="er-rule" />
        <div style={{ padding: 26 }}>
          <p className="er-label">END EXAM</p>
          <p className="er-h2" style={{ marginTop: 12 }}>Submit this exam?</p>
          <p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.6, color: "var(--ink-4)" }}>{answered} of {total} answered. Unanswered questions count as incorrect. You will see your score report immediately.</p>
          <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button data-testid="exam-submit-cancel" onClick={onCancel} className="er-btn-secondary" style={{ flex: 1 }}>Keep working</button>
            <button data-testid="exam-submit-confirm" onClick={onConfirm} className="er-btn-dark" style={{ flex: 1 }}>Submit exam</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExamRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [cert, setCert] = useState(null);
  const [question, setQuestion] = useState(null);
  const [pos, setPos] = useState(1);
  const [remaining, setRemaining] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const submit = useCallback(async (auto = false) => {
    try {
      await api.post(`/exam-sessions/${id}/submit`);
      if (auto) toast.info("Time expired — exam submitted");
      navigate(`/report/${id}`);
    } catch (e) { toast.error(errMsg(e)); }
  }, [id, navigate]);

  useEffect(() => {
    api.get(`/exam-sessions/${id}`).then((r) => {
      if (r.data.status === "completed") return navigate(`/report/${id}`);
      setSession(r.data);
      setPos(r.data.current_position || 1);
      setRemaining(r.data.time_remaining_seconds);
      api.get(`/certs/${r.data.cert_slug}`).then((c) => setCert(c.data)).catch(() => {});
    }).catch((e) => toast.error(errMsg(e)));
  }, [id, navigate]);

  const hasSession = !!session;
  useEffect(() => {
    if (!hasSession) return;
    api.get(`/exam-sessions/${id}/questions/${pos}`).then((r) => setQuestion(r.data)).catch((e) => toast.error(errMsg(e)));
    api.patch(`/exam-sessions/${id}/position`, { position: pos }).catch(() => {});
  }, [pos, hasSession, id]);

  const timerActive = remaining != null;
  useEffect(() => {
    if (!timerActive) return;
    const t = setInterval(() => setRemaining((r) => { if (r <= 1) { clearInterval(t); submit(true); return 0; } return r - 1; }), 1000);
    return () => clearInterval(t);
  }, [timerActive, submit]);

  const answer = async (idx) => {
    const cur = session.answers[String(pos)];
    const next = cur === idx ? null : idx;
    setSession((s) => ({ ...s, answers: { ...s.answers, [String(pos)]: next === null ? undefined : next } }));
    await api.patch(`/exam-sessions/${id}/answer`, { question_number: pos, answer_index: next }).catch(() => {});
  };
  const toggleMark = async () => {
    const marked = session.marked.includes(pos);
    setSession((s) => ({ ...s, marked: marked ? s.marked.filter((m) => m !== pos) : [...s.marked, pos] }));
    await api.patch(`/exam-sessions/${id}/mark`, { question_number: pos, marked: !marked }).catch(() => {});
  };

  if (!session || !question) return <div className="er"><div className="er-rule" /><p style={{ padding: 32, color: "var(--ink-5)" }}>Loading exam…</p></div>;
  const answered = Object.keys(session.answers).filter((k) => session.answers[k] !== undefined && session.answers[k] !== null).length;
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const flagged = session.marked.includes(pos);
  const last = pos >= session.total_questions;

  return (
    <div className="er" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }} data-testid="exam-runner">
      <div className="er-rule" />
      <div className="er-exam-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 20, minWidth: 0 }}>
          <Wordmark size={17} img={26} to="/dashboard" testid="exam-logo" />
          <span style={{ width: 1, height: 22, background: "var(--hair)" }} className="er-desktop-only" />
          <span style={{ fontSize: 14, color: "var(--ink-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="exam-title">{examTitle(cert, session)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
          <span className="er-mono" style={{ fontSize: 13, color: "var(--ink-4)", whiteSpace: "nowrap" }} data-testid="exam-progress">QUESTION {pos} / {session.total_questions}</span>
          {remaining != null && (
            <span className="er-timer"><span className="er-label" style={{ fontSize: 10, color: "var(--ac-deep)" }}>TIME LEFT</span><span className="er-mono" style={{ fontSize: 15, color: "var(--ac-deep)" }} data-testid="exam-timer">{fmt(remaining)}</span></span>
          )}
          <button data-testid="exam-end-btn" onClick={() => setConfirming(true)} className="er-link-btn er-desktop-only" style={{ fontSize: 14, color: "var(--ink-4)" }}>End exam</button>
        </div>
      </div>
      <div className="er-progress"><div style={{ width: `${(pos / session.total_questions) * 100}%` }} /></div>

      <div className="er-exam-body er-fade" key={pos} style={{ flex: 1, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <p className="er-label" data-testid="exam-domain">DOMAIN {cert?.tokens?.domains?.find((d) => d.name === question.domain)?.id || "·"} {String(question.domain || "").toUpperCase()}</p>
          <button data-testid="exam-flag-btn" onClick={toggleMark} className={`er-flag${flagged ? " on" : ""}`}><span className="sq" />{flagged ? "Flagged for review" : "Flag for review"}</button>
        </div>
        <p className="er-stem" data-testid="exam-question-stem">{question.stem}</p>
        <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 10 }}>
          {question.options.map((opt, i) => (
            <button key={`${question.question_id}-${i}`} data-testid={`exam-option-${i}`} onClick={() => answer(i)} className={`er-opt${session.answers[String(pos)] === i ? " sel" : ""}`}>
              <span className="ltr">{"ABCD"[i]}</span><span>{opt}</span>
            </button>
          ))}
        </div>
        {/* v1.20.5: Previous / Next sit right under the answers, in the question column, so the hand never travels to the page corners */}
        <div className="er-question-nav" data-testid="exam-question-nav">
          <button data-testid="exam-prev-btn" disabled={pos <= 1} onClick={() => setPos(pos - 1)} className="er-btn-secondary" style={{ fontSize: 14.5 }}>← Previous</button>
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-5)", textAlign: "center" }}>Select one answer. You can change it any time before you submit.</p>
          {last ? (
            <button data-testid="exam-finish-btn" onClick={() => setConfirming(true)} className="er-btn-dark" style={{ fontSize: 14.5 }}>Submit exam</button>
          ) : (
            <button data-testid="exam-next-btn" onClick={() => setPos(pos + 1)} className="er-btn" style={{ padding: "12px 26px", fontSize: 14.5 }}>Next →</button>
          )}
        </div>
      </div>

      <Navigator session={session} pos={pos} onJump={setPos} />

      <div className="er-exam-foot" style={{ justifyContent: "center" }}>
        <button data-testid="exam-submit-btn" onClick={() => setConfirming(true)} className="er-link-btn" style={{ fontSize: 14, textDecoration: "underline" }}>Review and submit</button>
      </div>

      {confirming && <SubmitDialog answered={answered} total={session.total_questions} onCancel={() => setConfirming(false)} onConfirm={() => submit()} />}
    </div>
  );
}
